import { useQuery } from 'react-query';

import { useGetReviewEventBannerByProductIdQuery } from '@widgets/review-event-banner';

import { getPdpIntegratedData, getReviewPredictionSummary } from '../graphql/pdp-integrated-data.2';
import {
  GetPdpIntegratedData_pdp_base_info,
  GetPdpIntegratedData_pdp_base_info_fitting_model_list_item_list as FittingModel,
  GetPdpIntegratedData_related_product_review_summary,
  GetPdpIntegratedData_ux_review_list,
  UxReviewListOrderType,
} from '../graphql/pdp-integrated-data_types.2';
import { queryOptions } from '../lib/queryHelpers';
import { checkValidCatalogProductId } from '../lib/utils';

export const PDP_INTEGRATED_DATA_QUERY_KEY = 'getPdpIntegratedData';
export function toFittingModelList(data?: GetPdpIntegratedData_pdp_base_info | null): FittingModel[] {
  return data?.fitting_model_list?.item_list ?? [];
}

export interface GetPdpIntegratedDataParams {
  catalog_product_id: string;
}

export const useGetPdpIntegratedData = ({ catalog_product_id, ...restParams }: GetPdpIntegratedDataParams) => {
  const mergedParams = {
    catalog_product_id,
    limit_count: 5,
    order: UxReviewListOrderType.EXPERIENCE_GROUP_BEST_SCORE_DESC,
    ...restParams,
  };
  const { data, ...queryResponse } = useQuery(
    [PDP_INTEGRATED_DATA_QUERY_KEY, catalog_product_id],
    () => getPdpIntegratedData(mergedParams, { show_alert: false }),
    {
      ...queryOptions,
      enabled: checkValidCatalogProductId(catalog_product_id),
    },
  );
  const { data: reviewEventData } = useGetReviewEventBannerByProductIdQuery({ catalogProductId: catalog_product_id });

  const review_count = data?.data.related_product_review_summary.all_count;
  const has_review_event = !!reviewEventData?.simple_banner?.id;
  const enabledPredictionRating =
    checkValidCatalogProductId(catalog_product_id) && review_count === 0 && !has_review_event;

  const { data: predictionSummaryData, isSuccess: isPredictSuccessed } = useQuery(
    ['getReviewPredictionRating', catalog_product_id],
    async () => {
      const {
        data: { ux_review_prediction_summary },
      } = await getReviewPredictionSummary({ product_id: catalog_product_id }, { show_alert: false });
      return {
        ux_review_prediction_summary,
      };
    },
    {
      ...queryOptions,
      enabled: enabledPredictionRating,
    },
  );

  const hasReviewPredictSummary = !!predictionSummaryData?.ux_review_prediction_summary;
  const hasPredictionRating =
    review_count === 0 && !has_review_event && predictionSummaryData?.ux_review_prediction_summary?.rating;

  return {
    ...queryResponse,
    pdp_size_info: data?.data.pdp_size_info,
    size_recommendation: data?.data?.pdp_base_info?.size_recommendation,
    related_product_review_summary: data?.data
      .related_product_review_summary as GetPdpIntegratedData_related_product_review_summary,
    fitting_model_list: toFittingModelList(data?.data.pdp_base_info),
    short_form_contents: data?.data.pdp_short_form_contents?.content_list,
    ux_review_list: data?.data.ux_review_list as GetPdpIntegratedData_ux_review_list,
    epick_list: data?.data.pdp_base_info?.epick_list ?? [],
    extra_notice_banner: data?.data.pdp_base_info?.catalog_product.extra_notice_banner,
    policy_list: data?.data.pdp_base_info?.policy_list ?? [],
    matching_catalog_product_info: data?.data.pdp_option_info?.catalog_product.matching_catalog_product_info ?? null,
    review_prediction_rating: hasPredictionRating ? predictionSummaryData?.ux_review_prediction_summary?.rating : null,
    hasReviewPredictSummary,
    enabledPredictionRating,
    isPredictSuccessed,
    catalog_product: data?.data?.pdp_base_info?.catalog_product,
  };
};
